import { zodResolver } from '@hookform/resolvers/zod';
import { Flex } from '@ironhack/design-system2/components/flex';
import { FormFeedback } from '@ironhack/design-system2/components/form-feedback';
import { omit, pick } from 'ramda';
import { type ReactElement, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useCookie, useSessionStorage } from 'react-use';

import { ContactDetailsFields } from '@/app/components/client/contact-details-fields';
import { FormSubmit } from '@/app/components/server/form-submit';
import { usePageContext } from '@/contexts/page-context/use-page-context';
import { useFormRequest } from '@/hooks/use-form-request';
import { useGtmLocalStorage } from '@/hooks/use-gtm-local-storage';
import { sendEvent } from '@/lib/gtm';
import { TurnstileFormToken } from '@/lib/turnstile/components';
import { regionToCampusMap } from '@/lib/utils';
import { getFinancingSchema } from '@/lib/utils/form-validations/forms/financing-form';
import { type EmailInput } from '@/lib/utils/form-validations/schemas/email-schema';
import { type NameInputs } from '@/lib/utils/form-validations/schemas/name-schema';
import {
  type PhoneInputs,
  getPhoneDefaults,
} from '@/lib/utils/form-validations/schemas/phone-schema';
import { urlToDatoLocale } from '@/lib/utils/locale';
import { type Form as FinancingFormType } from '../../modules/1-form-full';

type FormData = {
  cToken: string;
  newsletter: boolean;
  phone: PhoneInputs | undefined;
  referral: string;
} & EmailInput &
  NameInputs;

type Props = {
  onSuccess: () => void;
  financingForm: FinancingFormType;
};

export const Form = (props: Props): ReactElement => {
  const { financingForm, onSuccess } = props;
  const {
    buttonText,
    errorMessage,
    legalText,
    newsletterLabel,
    withPhone = false,
  } = financingForm;
  const [isGoogleSubmitting, setIsGoogleSubmitting] = useState(false);
  const [financingOption] = useSessionStorage('financingOption', 'Ironhack');
  const {
    params: { language, region },
  } = usePageContext();
  const [campus] = useSessionStorage('campus', regionToCampusMap[region][0]);

  const formSchema = getFinancingSchema(financingForm);

  const formMethods = useForm<FormData>({
    defaultValues: {
      newsletter: false,
      phone: withPhone
        ? getPhoneDefaults(region)
        : // eslint-disable-next-line no-undefined
          undefined,
      referral: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    resolver: zodResolver(formSchema),
  });
  const {
    formState: { isSubmitting },
    handleSubmit,
    setValue,
  } = formMethods;
  const [formFeedback, setFormFeedback] = useState<{
    status: 'error';
    message: string;
  } | null>();
  const formRequest = useFormRequest();
  const [, setGtmLocalStorage] = useGtmLocalStorage();
  const [countryName] = useCookie('ss_country');
  const setCaptchaToken = (token: string) => setValue('cToken', token);

  const onSubmit = async (formData: FormData): Promise<void> => {
    setFormFeedback(null);
    try {
      await formRequest({
        data: {
          ...omit(['newsletter'], formData),
          language,
          campus,
          emailOptIn: formData.newsletter,
          financingOption,
        },
        formType: 'financing',
      });

      sendEvent({
        email: formData.email,
        eventAction: 'send financing request',
        eventCategory: 'Financing',
        eventLabel: `${financingOption}::${region}`,
        pageType: 'Financing',
      });
      setGtmLocalStorage({
        applicationData: pick(['email', 'firstName', 'lastName'], formData),
        countryName:
          countryName && countryName !== 'undefined'
            ? countryName
            : urlToDatoLocale[`${region}${language}`].slice(-2),
      });
      onSuccess();
    } catch {
      setFormFeedback({ status: 'error', message: errorMessage });
    }
  };

  return (
    <Flex direction="column">
      <FormProvider {...formMethods}>
        <form id="financeForm" noValidate onSubmit={handleSubmit(onSubmit)}>
          <Flex alignItems="center" direction="column">
            <ContactDetailsFields
              fields={pick(
                [
                  'chooseBetween',
                  'completeFields',
                  'emailLabel',
                  'emailPlaceholder',
                  'firstNameLabel',
                  'firstNamePlaceholder',
                  'lastNameLabel',
                  'lastNamePlaceholder',
                  'phoneLabel',
                  'phonePlaceholder',
                ],
                financingForm
              )}
              mt={4}
              onGoogleClick={() => {
                setFormFeedback(null);
                setIsGoogleSubmitting(true);
              }}
              onGoogleError={() => {
                setIsGoogleSubmitting(false);
                setFormFeedback({ status: 'error', message: errorMessage });
              }}
              onGoogleSuccess={() => {
                setIsGoogleSubmitting(false);
                void handleSubmit(onSubmit)();
              }}
              withPhone={withPhone}
            />

            <TurnstileFormToken
              language={language}
              onVerify={setCaptchaToken}
            />
            <FormSubmit
              buttonText={buttonText}
              isLoading={isSubmitting || isGoogleSubmitting}
              legalText={legalText}
              newsletterLabel={newsletterLabel}
              withNewsletter
            />
            {formFeedback?.status && <FormFeedback mt={2} {...formFeedback} />}
          </Flex>
        </form>
      </FormProvider>
    </Flex>
  );
};
